import { CAST_AI } from '@cast/constants';
import { Alert, ExternalLink, Typography } from '@cast/design-system';
import { getDocsLink, notReachable } from '@cast/utils';

import { EvictorConfig } from '@cast/types-workload-eviction';
import {
  isAgentVersionSufficientForEvictor,
  REQUIRED_AGENT_VERSION_FOR_EVICTOR,
} from '@cast/split-me/utils/cluster';

const getEvictorSettingsAlertState = ({
  evictorConfig,
  clusterAgentVersion,
}: Props):
  | { type: 'evictor-incompatible' }
  | { type: 'evictor-missing' }
  | { type: 'outdated-agent'; requiredVersion: string }
  | { type: 'no-alert' } => {
  switch (evictorConfig.status) {
    case 'EVICTOR_STATUS_INCOMPATIBLE':
      return { type: 'evictor-incompatible' };
    case 'EVICTOR_STATUS_MISSING':
      return { type: 'evictor-missing' };
  }

  if (
    clusterAgentVersion &&
    !isAgentVersionSufficientForEvictor(clusterAgentVersion)
  ) {
    return {
      type: 'outdated-agent',
      requiredVersion: REQUIRED_AGENT_VERSION_FOR_EVICTOR,
    };
  }

  return { type: 'no-alert' };
};

type Props = {
  evictorConfig: EvictorConfig;
  clusterAgentVersion: string | undefined;
};

export const EvictorSettingsAlert = ({
  evictorConfig,
  clusterAgentVersion,
}: Props) => {
  const alertState = getEvictorSettingsAlertState({
    evictorConfig,
    clusterAgentVersion,
  });

  switch (alertState.type) {
    case 'outdated-agent':
      return (
        <Alert color="advisory" testId="upgrade-agent-alert" size="sm">
          <Typography component="span">
            This feature works with {CAST_AI} agent v
            {alertState.requiredVersion} or later. To check the version and
            upgrade if necessary, follow the{' '}
            <ExternalLink link={getDocsLink('docs_upgrading_agent')}>
              documentation.
            </ExternalLink>
          </Typography>
        </Alert>
      );
    case 'evictor-incompatible':
      return (
        <Alert
          color="advisory"
          testId="evictor-incompatible-alert"
          size="sm"
          title={`Bin-packing is installed but it is not managed by ${CAST_AI}.`}
        >
          <Typography>
            Bin-packing configuration using policies is not currently possible
            due to compatibility issues. To troubleshoot, please refer to the{' '}
            <ExternalLink link={getDocsLink('docs_evictor')}>
              documentation.
            </ExternalLink>
          </Typography>
        </Alert>
      );
    case 'evictor-missing':
      return (
        <Alert color="advisory" testId="evictor-missing-alert" size="sm">
          <Typography>
            Bin-packing not found. The deployment is either absent from the
            cluster or has not yet been scaled. For more information, refer to
            the{' '}
            <ExternalLink link={getDocsLink('docs_evictor')} noIcon>
              documentation.
            </ExternalLink>
          </Typography>
        </Alert>
      );
    case 'no-alert':
      return null;
    default:
      return notReachable(alertState);
  }
};
